/**
 * ROZJAZD KONFIGURACJI PRZEŁOŻONY NA ZDANIE, KTÓRE PROWADZI DO DZIAŁANIA.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POWSTAŁO Z UWAGI Z WDROŻENIA (28.08.2026): „to taka ściana tekstu i to z tabelami, że
 *  ciężko się to czyta". Baner wypisywał osiem wierszy w postaci
 *
 *      DrawerChannels: dynabox-sdi=104 · chmura-mssql-dbw=47 · sdi-bipoint=47 · temrexlockerbox=47
 *
 *  czyli CZTERY RÓWNOWAŻNIE WYGLĄDAJĄCE PARY, z których człowiek sam musiał wyliczyć, która
 *  maszyna ma prawdę i ilu wierszy brakuje pozostałym. Przy ośmiu tabelach i czterech węzłach
 *  to trzydzieści dwie liczby do porównania wzrokiem.
 *
 *  ⚠️ TU LICZY SIĘ JEDNO PYTANIE: „SKĄD pójdą zmiany i CZEGO brakuje komu". Dlatego ta funkcja
 *  wyciąga źródło (węzeł z pełnym obrazem) i braki (kto ile mniej), a ekran pokazuje resztę jako
 *  szczegół, nie jako równorzędną treść.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

export type RodzajRozjazdu = 'BRAK' | 'LICZBY' | 'TRESC';

export type WezelRozjazdu = { nodeId: string; wierszy: number; skrot?: string | null };

export type OpisRozjazdu = {
  rodzaj: RodzajRozjazdu;
  /**
   * WĘZEŁ Z PEŁNYM OBRAZEM — z niego pójdą zmiany przy wyrównaniu.
   *
   * ⚠️ `null` PRZY ROZJEŹDZIE TREŚCI, i to nie jest brak danych. Tam liczby są RÓWNE, więc
   * „najbogatszy" nie istnieje — wskazanie któregokolwiek węzła jako źródła byłoby zmyśleniem,
   * a wyrównanie i tak tego nie prostuje.
   */
  zrodlo: { nodeId: string; wierszy: number } | null;
  /** Kto ile ma MNIEJ niż źródło. Puste przy rozjeździe treści. */
  braki: { nodeId: string; brakuje: number; wcale: boolean }[];
  /**
   * KTO MA WIERSZE PONAD TO, CO MA WŁAŚCICIEL — i to jest osobna kategoria, nie „ujemny brak".
   *
   * ⚠️⚠️ ZMIERZONE NA TESTÓWCE: dla VMID 3 BiPoint ma 15 pozycji planogramu, a właściciel
   * (Lockerbox) 14 — BiPoint trzyma jedną ZJAWĘ z chwili klonowania wyspy. Dopóki ekran liczył
   * braki względem NAJBOGATSZEGO węzła, ogłaszał „brakuje 1 u właściciela" — czyli wskazywał palcem
   * na maszynę, która jako jedyna ma rację.
   *
   * ⚠️ WYRÓWNANIE TEGO NIE USUWA: dosypuje brakujące, nigdy nie kasuje nadmiarowych. Ekran musi
   * to rozróżniać, żeby nie obiecywać sprzątnięcia zjawy.
   */
  nadmiar: { nodeId: string; ponad: number }[];
  /**
   * TE SAME BRAKI, ZGRUPOWANE PO LICZBIE — i to jest różnica między czytelnym wierszem
   * a szumem.
   *
   * ⚠️⚠️ NA WYSPIE CZTEROWĘZŁOWEJ TRZY WĘZŁY BRAKUJE ZWYKLE TYLE SAMO, bo wszystkie są kopią
   * tego samego obrazu wdrożeniowego. Wypisywanie „brakuje 57" trzy razy pod rząd, w ośmiu
   * wierszach, to dwadzieścia cztery powtórzenia tej samej liczby — dokładnie ta ściana tekstu,
   * o której było zgłoszenie. Jedna liczba i lista nazw mówi tyle samo w jednej linijce.
   */
  grupy: { brakuje: number; wcale: boolean; wezly: string[] }[];
};

/**
 * RODZAJ WYLICZONY Z LICZB, gdy portal go nie przysłał.
 *
 * ⚠️ STARSZA INSTANCJA NIE ODDA POLA `rodzaj`, a ekran ma się wtedy narysować, nie wywalić.
 * Ta sama reguła co po stronie API (`geometria-rozjazd.ts`), świadomie powtórzona — bo alternatywą
 * byłoby pokazywanie „nie wiem" w miejscu, w którym da się to policzyć z tego, co przyszło.
 */
export function rodzajZLiczb(wezly: readonly WezelRozjazdu[], podany?: RodzajRozjazdu): RodzajRozjazdu {
  if (podany !== undefined) return podany;
  const liczby = new Set(wezly.map((w) => w.wierszy));
  if (liczby.size === 1) return 'TRESC';
  return wezly.some((w) => w.wierszy === 0) ? 'BRAK' : 'LICZBY';
}

/**
 * ⚠️⚠️ PRAWDĄ JEST WŁAŚCICIEL AUTOMATU, NIE NAJBOGATSZY WĘZEŁ — i to jest poprawka błędu, który
 * ten ekran pokazywał na żywo (28.08.2026). Dla VMID 3 BiPoint ma 15 pozycji, a właściciel
 * (Lockerbox) 14, bo BiPoint trzyma zjawę z chwili klonowania. Ekran ogłaszał wtedy „pełny obraz
 * ma BiPoint" i „brakuje 1 u temrexlockerbox" — czyli miał wszystko na odwrót.
 *
 * Wyrównanie buduje z read-modelu, a read-model powstaje z danych WŁAŚCICIELA (`odsiejObceWiersze`
 * odrzuca w górę wiersze o cudzych automatach). Więc źródłem jest właściciel i tylko on.
 *
 * ⚠️ `wlasciciel` NIEZNANY (starsza instancja albo żaden węzeł nie ma tego VMID) — wtedy wracamy do
 * najbogatszego, ale to jest DOMYSŁ, nie wiedza, i ekran nie ma prawa nazywać go źródłem.
 */
export function opisRozjazdu(
  wezly: readonly WezelRozjazdu[],
  podanyRodzaj?: RodzajRozjazdu,
  wlasciciel?: string | null,
): OpisRozjazdu {
  const rodzaj = rodzajZLiczb(wezly, podanyRodzaj);
  if (rodzaj === 'TRESC') return { rodzaj, zrodlo: null, braki: [], grupy: [], nadmiar: [] };

  const uWlasciciela = wlasciciel == null ? undefined : wezly.find((w) => w.nodeId === wlasciciel);
  /** Najbogatszy węzeł — używany TYLKO, gdy właściciel jest nieznany. */
  const najlepszy =
    uWlasciciela ?? [...wezly].sort((a, b) => b.wierszy - a.wierszy || a.nodeId.localeCompare(b.nodeId))[0];
  if (najlepszy === undefined) return { rodzaj, zrodlo: null, braki: [], grupy: [], nadmiar: [] };

  const braki = wezly
    .filter((w) => w.wierszy < najlepszy.wierszy)
    .map((w) => ({ nodeId: w.nodeId, brakuje: najlepszy.wierszy - w.wierszy, wcale: w.wierszy === 0 }))
    .sort((a, b) => b.brakuje - a.brakuje || a.nodeId.localeCompare(b.nodeId));

  const poLiczbie = new Map<number, { brakuje: number; wcale: boolean; wezly: string[] }>();
  for (const b of braki) {
    const g = poLiczbie.get(b.brakuje);
    if (g === undefined) poLiczbie.set(b.brakuje, { brakuje: b.brakuje, wcale: b.wcale, wezly: [b.nodeId] });
    else g.wezly.push(b.nodeId);
  }
  const grupy = [...poLiczbie.values()].sort((a, b) => b.brakuje - a.brakuje);

  const nadmiar = wezly
    .filter((w) => w.wierszy > najlepszy.wierszy)
    .map((w) => ({ nodeId: w.nodeId, ponad: w.wierszy - najlepszy.wierszy }))
    .sort((a, b) => b.ponad - a.ponad || a.nodeId.localeCompare(b.nodeId));

  return { rodzaj, zrodlo: { nodeId: najlepszy.nodeId, wierszy: najlepszy.wierszy }, braki, grupy, nadmiar };
}

/**
 * JEDNO ŹRÓDŁO DLA CAŁEGO AUTOMATU — do zdania „zmiany pójdą z tej maszyny".
 *
 * ⚠️⚠️ WSZYSTKIE WIERSZE BANERA DOTYCZĄ JEDNEGO AUTOMATU (czujnik jest odpytywany po `vmid`
 * tej karty), więc źródłem powinien być ZA KAŻDYM RAZEM ten sam węzeł — jego właściciel.
 * Zwracamy nazwę TYLKO wtedy, gdy tak faktycznie jest.
 *
 * ⚠️ `null`, GDY WĘZŁY SIĘ NIE ZGADZAJĄ CO DO ŹRÓDŁA. Taki stan znaczy, że różne tabele mają
 * najbogatsze różne maszyny — czyli że nie ma jednej maszyny z prawdą i wyrównanie nie jest
 * prostą operacją. Wypisanie wtedy którejkolwiek nazwy w pogrubieniu byłoby wskazaniem palcem
 * na maszynę, która wcale nie jest źródłem.
 */
export function zrodloAutomatu(
  rozjazdy: readonly { wezly: readonly WezelRozjazdu[]; rodzaj?: RodzajRozjazdu }[],
): string | null {
  /**
   * ⚠️⚠️ UŻYWAĆ WYŁĄCZNIE, GDY PORTAL NIE POWIEDZIAŁ, KTO JEST WŁAŚCICIELEM. Ta funkcja zgaduje
   * po liczbie wierszy, a więcej wierszy NIE ZNACZY PRAWDA — zmierzone: BiPoint ma dla VMID 3
   * jedną pozycję więcej niż właściciel, bo trzyma zjawę z chwili klonowania wyspy. Gdy
   * `wlascicielWezel` przychodzi z API, bierzemy jego i tej funkcji nie wołamy.
   */
  const zrodla = new Set(
    rozjazdy
      .map((r) => opisRozjazdu(r.wezly, r.rodzaj).zrodlo?.nodeId)
      .filter((n): n is string => n !== undefined),
  );
  return zrodla.size === 1 ? [...zrodla][0]! : null;
}

/**
 * WĘZŁY POGRUPOWANE PO SKRÓCIE TREŚCI — dla rozjazdu, w którym liczby są RÓWNE.
 *
 * ⚠️⚠️ POWSTAŁO Z BEZUŻYTECZNEJ TABELKI (01.09.2026). Okno rozjazdu pokazywało przy rozjeździe
 * treści listę „węzeł → liczba wierszy" dla trzynastu węzłów — i trzynaście razy TĘ SAMĄ liczbę,
 * bo przy rozjeździe treści liczby się zgadzają z definicji. Zero informacji, trzynaście wierszy.
 *
 * ⚠️ TO, CO NIESIE INFORMACJĘ, TO SKRÓT: węzły o tym samym skrócie mają identyczną treść, więc
 * grupy odpowiadają na jedyne sensowne pytanie — KTÓRY węzeł odstaje od reszty.
 *
 * ⚠️ `null` (węzeł skrótu nie podał) TO OSOBNA GRUPA, nie „inna treść". Starszy agent skrótów nie
 * liczy; wrzucenie go do którejkolwiek grupy byłoby zmyśleniem.
 */
export function grupyTresci(
  wezly: readonly WezelRozjazdu[],
): { skrot: string | null; wezly: string[] }[] {
  const grupy = new Map<string, { skrot: string | null; wezly: string[] }>();
  for (const w of wezly) {
    const skrot = w.skrot ?? null;
    const klucz = skrot ?? '(bez skrótu)';
    const g = grupy.get(klucz);
    if (g === undefined) grupy.set(klucz, { skrot, wezly: [w.nodeId] });
    else g.wezly.push(w.nodeId);
  }
  return [...grupy.values()].sort((a, b) => b.wezly.length - a.wezly.length);
}
